// Thin fetch wrappers for /sources endpoints (PRD 06 §10). Mockable via
// `vi.spyOn(globalThis, "fetch")` in tests.
//
// TWO BUGS FIXED HERE when /sources gained ownership gates, both of which
// meant this module could never have worked against a real backend:
//
//   1. Every URL was RELATIVE ("/sources"), so it resolved against the SPA
//      origin rather than API_BASE and never reached the API at all in a
//      split-port dev setup.
//   2. No credential was sent. The header comment used to say "the dashboard
//      caller owns those concerns"; no caller ever did.
//
// Both are now the shared client's job (`lib/apiClient`), which attaches the
// session cookie and `X-mAiSight-Key` and resolves API_BASE.

import { apiFetch } from "../lib/apiClient";
import type {
  CreateSourceBody,
  SourceRecord,
  SourceSyncRun,
} from "./types";

async function jsonOrThrow<T>(r: Response, verb: string): Promise<T> {
  if (!r.ok) throw new Error(`${verb} failed: ${r.status}`);
  return (await r.json()) as T;
}

/** The signed-in caller's org.
 *
 *  A plain fetch rather than `screens/ops/api.ts::useMe`: this module is
 *  deliberately built on useState + useEffect (see SourcesListPage's header),
 *  and reaching for a react-query hook would drag a `QueryClientProvider`
 *  requirement into every consumer and its tests.
 */
export async function currentOrgSlug(): Promise<string> {
  const r = await apiFetch("/auth/me");
  return (await jsonOrThrow<{ org_slug: string }>(r, "GET /auth/me")).org_slug;
}

/** `GET /sources` requires `org_slug`; omitting it was a 422, not a
 *  "list everything". A caller may only list its own org — the backend
 *  returns `[]` for any other. */
export async function listSources(orgSlug: string): Promise<SourceRecord[]> {
  const r = await apiFetch(`/sources?org_slug=${encodeURIComponent(orgSlug)}`);
  return jsonOrThrow<SourceRecord[]>(r, "GET /sources");
}

export async function getSource(id: string): Promise<SourceRecord> {
  const r = await apiFetch(`/sources/${encodeURIComponent(id)}`);
  return jsonOrThrow<SourceRecord>(r, "GET /sources/:id");
}

export async function createSource(body: CreateSourceBody): Promise<SourceRecord> {
  const r = await apiFetch("/sources", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow<SourceRecord>(r, "POST /sources");
}

export async function syncNow(id: string): Promise<{ run_id: string }> {
  const r = await apiFetch(`/sources/${encodeURIComponent(id)}/sync`, {
    method: "POST",
  });
  return jsonOrThrow<{ run_id: string }>(r, "POST /sources/:id/sync");
}

export async function listRuns(id: string): Promise<SourceSyncRun[]> {
  const r = await apiFetch(`/sources/${encodeURIComponent(id)}/runs`);
  return jsonOrThrow<SourceSyncRun[]>(r, "GET /sources/:id/runs");
}

export async function deleteSource(id: string): Promise<void> {
  const r = await apiFetch(`/sources/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!r.ok) throw new Error(`DELETE /sources/:id failed: ${r.status}`);
}

// Optional verify endpoint; backend may not implement it. Callers should
// catch errors and treat them as "verify unavailable" rather than failures.
export async function verifySource(
  type: string,
  config: Record<string, unknown>,
  auth_value: string,
): Promise<{ ok: boolean; message?: string; item_count?: number }> {
  const r = await apiFetch("/sources/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type, config, auth_value }),
  });
  return jsonOrThrow(r, "POST /sources/verify");
}
